import { AlertTriangle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export function MicroductPanel({ form, initial, allCables, selCables, toggleCable, tubeMap, setTubeMap, freeTubesList, assignCables, assigning }) {
  const { t } = useI18n();
  return (
    <div className="col-span-2 border-t border-slate-700 pt-3 mt-1" data-testid="microduct-occupancy">
      <span className="text-xs text-cyan-400 font-semibold uppercase tracking-wide">{t("Zajętość mikrorur")}</span>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {Array.from({ length: Number(form.tube_count) }, (_, i) => i + 1).map((n) => {
          const cab = allCables.find((c) => c.microduct_id === initial?.id && String(c.microduct_tube) === String(n));
          return (
            <div key={n} data-testid={`tube-${n}`} title={cab ? `${t("Kabel")} ${cab.number}` : t("wolna")}
              className={`w-8 h-8 rounded flex items-center justify-center text-[11px] font-mono border ${cab ? "bg-red-500/20 border-red-500 text-red-200" : "bg-green-500/15 border-green-600 text-green-200"}`}>
              {n}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-2">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block bg-green-500/40 border border-green-600" />{t("wolna")}</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block bg-red-500/40 border border-red-500" />{t("zajęta (kabel)")}</span>
      </div>
      {initial?.id && (
        <div className="mt-3 border-t border-slate-700 pt-2" data-testid="microduct-assign">
          <div className="text-[11px] text-slate-400 mb-1">{t("Przypisz kable do wolnych mikrorur")}</div>
          <div className="max-h-24 overflow-y-auto space-y-1 pr-1">
            {allCables.filter((c) => c.microduct_id !== initial.id).map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" className="accent-amber-500" data-testid={`assign-cable-${c.id}`}
                  checked={selCables.has(c.id)} onChange={() => toggleCable(c.id)} />
                {c.number}{c.microduct_id ? ` (${t("inna mikrokan.")})` : ""}
              </label>
            ))}
            {!allCables.filter((c) => c.microduct_id !== initial.id).length && <span className="text-[11px] text-slate-500">{t("Brak dostępnych kabli")}</span>}
          </div>
          {selCables.size > 0 && (
            <div className="mt-2 space-y-1" data-testid="assign-preview">
              <div className="text-[11px] text-slate-400">{t("Przydział mikrorur (możesz zmienić numer)")}:</div>
              {[...selCables].map((cid) => {
                const c = allCables.find((x) => x.id === cid);
                if (!c) return null;
                return (
                  <div key={cid} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 truncate text-slate-300">{c.number}</span>
                      <span className="text-slate-400">{t("mikrorura")}</span>
                      <input type="number" min="1" max={form.tube_count} data-testid={`assign-tube-${cid}`}
                        value={tubeMap[cid] ?? ""} onChange={(e) => setTubeMap((m) => ({ ...m, [cid]: e.target.value }))}
                        className="w-16 bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-xs" />
                    </div>
                    {(() => {
                      const cnt = Number(c.fiber_count || 12);
                      const fmap = {}; (c.fibers || []).forEach((f) => { if (f.number != null) fmap[Number(f.number)] = f; });
                      const nums = Array.from({ length: cnt }, (_, i) => i + 1);
                      const freeN = nums.filter((n) => !(fmap[n] && (fmap[n].status === "zajęte" || fmap[n].status === "zajete"))).length;
                      return (
                        <div className="mt-0.5" data-testid={`fiber-strip-${cid}`}>
                          <span className="text-[10px] text-slate-400">{t("włókna")}: {freeN}/{cnt} {t("wolnych")}</span>
                          <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {nums.map((fn) => {
                              const f = fmap[fn];
                              const occ = f && (f.status === "zajęte" || f.status === "zajete");
                              return <span key={fn} title={`${t("Włókno")} ${fn}${occ ? ` — ${f.leads_to || t("zajęte")}` : ` — ${t("wolne")}`}`}
                                className={`w-3 h-3 rounded-sm ${occ ? "bg-red-500" : "bg-green-500"}`} />;
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
          {Number(form.tube_count) > 0 && selCables.size > freeTubesList().length && (
            <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-300" data-testid="assign-overflow-warning">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <span>{t("Uwaga: brak wystarczającej liczby wolnych mikrorur")} — {t("wolne")}: {freeTubesList().length}, {t("zaznaczone")}: {selCables.size}.</span>
            </div>
          )}
          <button type="button" data-testid="assign-cables-btn" disabled={assigning || !selCables.size} onClick={assignCables}
            className="mt-2 px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-slate-900 font-semibold text-xs">
            {t("Przydziel")} ({selCables.size})
          </button>
        </div>
      )}
    </div>
  );
}
